/**
 * Legal page - Privacy, Terms, Disclaimer, Third-party attributions.
 * Single page with on-page anchors so users can deep-link to a section.
 */

import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { t, type TranslationKey } from "@/lib/i18n";
import { useLocale } from "@/hooks/use-locale";

const SECTIONS: Array<{ id: string; labelKey: TranslationKey }> = [
  { id: "privacy",      labelKey: "lg.navPrivacy" },
  { id: "terms",        labelKey: "lg.navTerms" },
  { id: "disclaimer",   labelKey: "lg.navDisclaimer" },
  { id: "attributions", labelKey: "lg.navAttr" },
];

const ATTRIBUTIONS = [
  { name: "React",         license: "MIT",        url: "https://github.com/facebook/react" },
  { name: "Vite",          license: "MIT",        url: "https://github.com/vitejs/vite" },
  { name: "Turf.js",       license: "MIT",        url: "https://github.com/Turfjs/turf" },
  { name: "proj4js",       license: "MIT",        url: "https://github.com/proj4js/proj4js" },
  { name: "shpjs",         license: "MIT",        url: "https://github.com/calvinmetcalf/shapefile-js" },
  { name: "shp-write",     license: "ISC",        url: "https://github.com/mapbox/shp-write" },
  { name: "Leaflet",       license: "BSD-2",      url: "https://github.com/Leaflet/Leaflet" },
  { name: "react-leaflet", license: "Hippocratic",url: "https://github.com/PaulLeCam/react-leaflet" },
  { name: "Radix UI",      license: "MIT",        url: "https://github.com/radix-ui/primitives" },
  { name: "Tailwind CSS",  license: "MIT",        url: "https://github.com/tailwindlabs/tailwindcss" },
  { name: "Lucide Icons",  license: "ISC",        url: "https://github.com/lucide-icons/lucide" },
  { name: "OpenStreetMap", license: "ODbL",       url: "https://www.openstreetmap.org/copyright" },
];

const Legal = () => {
  useLocale(); // re-render on language change
  useEffect(() => { document.title = "Legal · Shp.Aid"; }, []);

  return (
    <div className="min-h-screen bg-background" style={{ padding: "32px 24px 60px" }}>
      <div style={{ maxWidth: 760, margin: "0 auto" }}>

        {/* Back link */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
          style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 16 }}
        >
          <ArrowLeft style={{ width: 12, height: 12 }} />
          {t("lg.back")}
        </Link>

        {/* Title */}
        <h1 style={{ fontSize: 32, fontWeight: 900, letterSpacing: "-0.02em", marginBottom: 6, color: "hsl(var(--foreground))" }}>
          {t("lg.title")}
        </h1>
        <p style={{ fontSize: 13, color: "hsl(var(--muted-foreground))", marginBottom: 24 }}>
          {t("lg.subtitle")}
        </p>

        {/* Section nav */}
        <nav style={{
          display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 32,
          padding: 12, borderRadius: 12, background: "hsl(var(--accent) / 0.4)",
        }}>
          {SECTIONS.map(s => (
            <a key={s.id} href={`#${s.id}`}
               style={{
                 padding: "4px 10px", borderRadius: 6, fontSize: 10, fontWeight: 700,
                 letterSpacing: "0.1em", textTransform: "uppercase",
                 background: "hsl(var(--card))", color: "hsl(var(--foreground))",
                 textDecoration: "none", border: "1px solid hsl(var(--border))",
               }}>
              {t(s.labelKey)}
            </a>
          ))}
        </nav>

        {/* ── Privacy ── */}
        <section id="privacy" style={{ marginBottom: 36, scrollMarginTop: 20 }}>
          <h2 style={sectionTitle}>{t("lg.navPrivacy")}</h2>
          <p style={updated}>{t("lg.effective")}</p>

          <p style={body}>
            <strong>{t("lg.pShortB")}</strong> {t("lg.pShort")}
          </p>

          <h3 style={h3}>{t("lg.pCollectH")}</h3>
          <p style={body}><strong>{t("lg.pCollect1B")}</strong> {t("lg.pCollect1")}</p>

          <p style={body}>{t("lg.pCollect2")}</p>

          <h3 style={h3}>{t("lg.pThirdH")}</h3>
          <ul style={ul}>
            <li>{t("lg.pThird1")}</li>
            <li>{t("lg.pThird2")}</li>
          </ul>

          <h3 style={h3}>{t("lg.pCookieH")}</h3>
          <p style={body}>{t("lg.pCookie")}</p>

          <h3 style={h3}>{t("lg.pRightsH")}</h3>
          <p style={body}>{t("lg.pRights")}</p>
        </section>

        {/* ── Terms ── */}
        <section id="terms" style={{ marginBottom: 36, scrollMarginTop: 20 }}>
          <h2 style={sectionTitle}>{t("lg.navTerms")}</h2>
          <p style={updated}>{t("lg.effective")}</p>

          <p style={body}>{t("lg.tIntro")}</p>

          <h3 style={h3}>{t("lg.t1H")}</h3>
          <p style={body}>{t("lg.t1")}</p>

          <h3 style={h3}>{t("lg.t2H")}</h3>
          <p style={body}>{t("lg.t2")}</p>

          <h3 style={h3}>{t("lg.t3H")}</h3>
          <ul style={ul}>
            <li>{t("lg.t3a")}</li>
            <li>{t("lg.t3b")}</li>
            <li>{t("lg.t3c")}</li>
          </ul>

          <h3 style={h3}>{t("lg.t4H")}</h3>
          <p style={body}>{t("lg.t4")}</p>
        </section>

        {/* ── Disclaimer ── */}
        <section id="disclaimer" style={{ marginBottom: 36, scrollMarginTop: 20 }}>
          <h2 style={sectionTitle}>{t("lg.navDisclaimer")}</h2>
          <p style={body}>
            <strong>{t("lg.dLead")}</strong>
          </p>
          <p style={body}>{t("lg.dBody")}</p>
          <ul style={ul}>
            <li>{t("lg.d1")}</li>
            <li>{t("lg.d2")}</li>
            <li>{t("lg.d3")}</li>
          </ul>
          <p style={body}>{t("lg.dEnd")}</p>
        </section>

        {/* ── Attributions ── */}
        <section id="attributions" style={{ marginBottom: 36, scrollMarginTop: 20 }}>
          <h2 style={sectionTitle}>{t("lg.navAttr")}</h2>
          <p style={body}>{t("lg.aIntro")}</p>
          <ul style={{ ...ul, listStyle: "none", paddingLeft: 0 }}>
            {ATTRIBUTIONS.map(a => (
              <li key={a.name} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px dashed hsl(var(--border))" }}>
                <a href={a.url} target="_blank" rel="noopener noreferrer"
                   style={{ color: "hsl(var(--brand-strong))", fontWeight: 600, textDecoration: "none" }}>
                  {a.name}
                </a>
                <span style={{ color: "hsl(var(--muted-foreground))", fontSize: 11, fontFamily: "ui-monospace, monospace" }}>
                  {a.license}
                </span>
              </li>
            ))}
          </ul>
        </section>

        <p style={{ fontSize: 10, color: "hsl(var(--muted-foreground))", textAlign: "center", marginTop: 32 }}>
          {t("lg.footer")}
        </p>
      </div>
    </div>
  );
};

const sectionTitle: React.CSSProperties = { fontSize: 22, fontWeight: 800, color: "hsl(var(--foreground))", marginBottom: 4, letterSpacing: "-0.01em" };
const h3:           React.CSSProperties = { fontSize: 14, fontWeight: 700, color: "hsl(var(--foreground))", marginTop: 18, marginBottom: 6 };
const body:         React.CSSProperties = { fontSize: 13, color: "hsl(var(--foreground))", lineHeight: 1.6, marginBottom: 8 };
const updated:      React.CSSProperties = { fontSize: 10, color: "hsl(var(--muted-foreground))", fontFamily: "ui-monospace, monospace", marginBottom: 12, letterSpacing: "0.06em" };
const ul:           React.CSSProperties = { ...body, paddingLeft: 18, marginBottom: 12 };

export default Legal;
